import { useNavigate } from "react-router-dom";
import { Stack } from "@mantine/core";
import { Alert, PageHeader, SectionCard } from "@xplatform/design-system";
import { IconBuilding } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { BreakdownRow } from "@models/adminMonitoring";
import { adminMonitoringApi } from "@services/adminMonitoringApi";
import { useApiResource } from "@hooks/monitoring/useApiResource";
import { useMonitoringFilters } from "@hooks/monitoring/useMonitoringFilters";
import { WindowToolbar } from "@features/Insights";
import { AdminTable, col } from "@components/AdminTable";

/**
 * Every organisation with metered usage in the window, most expensive first
 * (cross-organisation callers). One row per organisation and currency; a row
 * opens that organisation's usage.
 */
export const OrganisationsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const monitoring = useMonitoringFilters();
  const { filters, preset, custom, granularity } = monitoring;
  const window = { from: filters.from, to: filters.to };

  const dashboard = useApiResource(`orgs:${JSON.stringify(window)}`, (signal) =>
    adminMonitoringApi().dashboard(window, { granularity, splitBy: "organisation", recent: false }, signal));
  const rows = dashboard.data?.breakdowns.organisation?.rows ?? [];

  const open = (row: BreakdownRow) => {
    const params = new URLSearchParams({ range: preset });
    if (preset === "custom" && custom.from && custom.to) {
      params.set("from", custom.from);
      params.set("to", custom.to);
    }
    navigate(`/admin/organisations/${encodeURIComponent(row.value)}?${params}`);
  };

  const columns = [
    col.twoLine<BreakdownRow>("organisation", t("admin.insights.columns.organisation"), (r) => ({
      primary: r.label ?? r.value, secondary: r.label ? r.value : undefined,
    }), { mono: true }),
    col.number<BreakdownRow>("events", t("admin.insights.columns.events"), (r) => r.event_count),
    col.number<BreakdownRow>("users", t("admin.insights.kpi.users"), (r) => r.users_count),
    col.number<BreakdownRow>("unpriced", t("admin.insights.unpriced"), (r) => r.unpriced_count),
    col.text<BreakdownRow>("share", t("admin.organisations.share"), (r) => `${r.share}%`, { width: 96 }),
    col.money<BreakdownRow>("cost", t("admin.insights.columns.cost"), (r) => ({ amount: r.total_cost, currency: r.currency })),
  ];

  return (
    <Stack gap="md">
      <PageHeader
        title={t("admin.organisations.title")}
        description={t("admin.organisations.description")}
        icon={<IconBuilding size={28} />}
        actions={
          <WindowToolbar
            preset={preset}
            custom={custom}
            onPresetChange={monitoring.setPreset}
            onRefresh={() => { monitoring.refresh(); dashboard.reload(); }}
            loading={dashboard.loading}
          />
        }
      />
      {dashboard.error && (
        <Alert color={dashboard.forbidden ? "gray" : "red"} title={t("admin.insights.loadFailed")}>{dashboard.error}</Alert>
      )}
      <SectionCard>
        <AdminTable
          columns={columns}
          data={rows}
          getRowKey={(r) => `${r.value}-${r.currency}`}
          onRowClick={open}
          loading={dashboard.loading}
          emptyMessage={t("admin.organisations.empty")}
        />
      </SectionCard>
    </Stack>
  );
};
